import React, { useState, useEffect } from 'react';
import PageLayout from '../../../components/PageLayout';
import {
  Search,
  Select,
  SelectItem,
  Pagination,
} from 'carbon-components-react';
import { useDebounceState } from '../../../utils/useDebounceState';
import useSWR from 'swr';
import { formatDate, getQueryString } from '../../../utils/common.util';
import { useUrlState } from '../../../utils/useUrlState';
import { Plus } from 'react-feather';
import GrantExemptionModal from './GrantExemptionModal';
import RevokeExemptionModal from './RevokeExemptionModal';
import './report-lock-exemptions.styles.scss';

const initialFilters = {
  academicYearId: '',
  termId: '',
  classId: '',
  dateGrantedFrom: '',
  dateGrantedTo: '',
  dateRevokedFrom: '',
  dateRevokedTo: '',
  active: 'true',
};

export default function ReportLockExemptionsPage() {
  const [query, setQuery] = useUrlState(initialFilters);
  const [exemptionSearchTerm, setExemptionSearchTerm] = useState('');
  const [debouncedExemptionSearchTerm, setDebouncedExemptionSearchTerm] =
    useDebounceState('');

  useEffect(() => {
    setDebouncedExemptionSearchTerm(exemptionSearchTerm);
  }, [exemptionSearchTerm, setDebouncedExemptionSearchTerm]);

  useEffect(() => {
    setPaginate((p) => ({ ...p, page: 1 }));
  }, [debouncedExemptionSearchTerm]);

  const { data: allEnrolledStudents } = useSWR('student/list?status=Enrolled');
  const studentLookup =
    allEnrolledStudents?.reduce((map, s) => {
      map[s.id] = s;
      return map;
    }, {}) || {};

  const { data: academicYears } = useSWR('academic-year/list');
  const { data: allTerms } = useSWR(
    query.academicYearId
      ? `academic-year/terms/?academicYearId=${query.academicYearId}`
      : null,
  );
  const { data: classes } = useSWR('class/list');

  const [paginate, setPaginate] = useState({
    page: 1,
    pageSize: 20,
  });

  const {
    data: exemptionsData,
    isLoading,
    mutate,
    error,
  } = useSWR(
    `report-lock/exemptions?${getQueryString({
      ...query,
      query: debouncedExemptionSearchTerm,
      take: paginate.pageSize,
      skip: paginate.page - 1,
    })}`,
  );

  const exemptions = exemptionsData || [];

  const [isOpen, setIsOpen] = useState('');
  const [selectedExemption, setSelectedExemption] = useState(null);

  const selectedAcademicYear = academicYears?.find(
    (ay) => ay.id === Number(query.academicYearId),
  );

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Academic Year', to: '/academic-year' },
        ...(query.academicYearId
          ? [
              {
                label: selectedAcademicYear?.description,
                to: `/academic-year/${query.academicYearId}`,
              },
            ]
          : []),
        { label: `Exemptions`, active: true },
      ]}
      title={
        <div
          className="d-flex align-items-center flex-wrap"
          style={{ gap: '30px' }}
        >
          <h1 className="h-20">Academic Report Lock Exemptions</h1>
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn"
            style={{ gap: '4px' }}
            onClick={() => setIsOpen('grant')}
          >
            <Plus size="14" strokeWidth="2px" />
            <span>Grant Exemption</span>
          </button>
        </div>
      }
      isLoading={isLoading}
    >
      <div className="report-lock-exemptions">
        <div className="card-row-3 align-items-center">
          <Select
            labelText="Academic Year"
            onChange={(e) => {
              setQuery({ academicYearId: e.target.value, termId: '' });
              setPaginate({ page: 1, pageSize: 20 });
            }}
            value={query.academicYearId}
          >
            <SelectItem selected text="All" value="" />
            {academicYears?.map((ay) => (
              <SelectItem
                key={ay.id}
                text={ay.description}
                value={ay.id.toString()}
              />
            ))}
          </Select>

          <Select
            labelText="Term"
            onChange={(e) => {
              setQuery({ termId: e.target.value });
              setPaginate({ page: 1, pageSize: 20 });
            }}
            value={query.termId}
            disabled={!query.academicYearId}
          >
            <SelectItem selected text="All" value="" />
            {allTerms?.map((term) => (
              <SelectItem
                key={term.id}
                text={term.description}
                value={term.id.toString()}
              />
            ))}
          </Select>

          <Select
            labelText="Class"
            onChange={(e) => {
              setQuery({ classId: e.target.value });
              setPaginate({ page: 1, pageSize: 20 });
            }}
            value={query.classId}
          >
            <SelectItem selected text="All" value="" />
            {classes?.map((cls) => (
              <SelectItem
                key={cls.id}
                text={cls.description}
                value={cls.id.toString()}
              />
            ))}
          </Select>

          {/* <DatePicker
            dateFormat="Y-m-d"
            datePickerType="single"
            id="date-granted-from"
            onChange={(value) => {
              if (value.length > 0) {
                const date = [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-');
                setQuery({ dateGrantedFrom: date });
              } else {
                setQuery({ dateGrantedFrom: '' });
              }
              setPaginate({ page: 1, pageSize: 20 });
            }}
          >
            <DatePickerInput
              labelText="Date Granted From"
              value={query.dateGrantedFrom}
              placeholder="YYYY-MM-DD"
            />
          </DatePicker>

          <DatePicker
            dateFormat="Y-m-d"
            datePickerType="single"
            id="date-granted-to"
            onChange={(value) => {
              if (value.length > 0) {
                const date = [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-');
                setQuery({ dateGrantedTo: date });
              } else {
                setQuery({ dateGrantedTo: '' });
              }
              setPaginate({ page: 1, pageSize: 20 });
            }}
          >
            <DatePickerInput
              labelText="Date Granted To"
              value={query.dateGrantedTo}
              placeholder="YYYY-MM-DD"
            />
          </DatePicker>

          <DatePicker
            dateFormat="Y-m-d"
            datePickerType="single"
            id="date-revoked-from"
            onChange={(value) => {
              if (value.length > 0) {
                const date = [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-');
                setQuery({ dateRevokedFrom: date });
              } else {
                setQuery({ dateRevokedFrom: '' });
              }
              setPaginate({ page: 1, pageSize: 20 });
            }}
          >
            <DatePickerInput
              labelText="Date Revoked From"
              value={query.dateRevokedFrom}
              placeholder="YYYY-MM-DD"
            />
          </DatePicker>

          <DatePicker
            dateFormat="Y-m-d"
            datePickerType="single"
            id="date-revoked-to"
            onChange={(value) => {
              if (value.length > 0) {
                const date = [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-');
                setQuery({ dateRevokedTo: date });
              } else {
                setQuery({ dateRevokedTo: '' });
              }
              setPaginate({ page: 1, pageSize: 20 });
            }}
          >
            <DatePickerInput
              labelText="Date Revoked To"
              value={query.dateRevokedTo}
              placeholder="YYYY-MM-DD"
            />
          </DatePicker> */}

          <Select
            labelText="Status"
            onChange={(e) => {
              setQuery({ active: e.target.value });
              setPaginate({ page: 1, pageSize: 20 });
            }}
            value={query.active}
          >
            <SelectItem selected disabled text="Select Status" value="" />
            <SelectItem text="Active" value="true" />
            <SelectItem text="Inactive" value="false" />
          </Select>

          {/* <Combobox
            options={students ?? []}
            label="Search Student"
            customStyles={{ width: '100%' }}
            value={selectedStudent}
            onChange={(option) => {
              setQuery({ studentId: option ? option.id : '' });
              setSearchStudent('');
            }}
            onSearch={setSearchStudent}
            onClear={() => {
              setQuery({ studentId: '' });
              setSearchStudent('');
            }}
            loading={isStudentsLoading}
            renderSelected={(option) =>
              option
                ? `${option.firstName} ${option.lastName} - ${option.classDescription}`
                : ''
            }
            renderOption={(option) =>
              `${option.firstName} ${option.lastName} - ${option.classDescription}`
            }
            labelKey="fullName"
            valueKey="id"
            searchPlaceholder="Search students..."
            placeholder="Search a student"
          /> */}

          <Search
            id="search-exemptions"
            name="search-exemptions"
            labelText=""
            value={exemptionSearchTerm}
            onChange={(e) => setExemptionSearchTerm(e.target.value)}
            placeHolderText="Search..."
            className="mt-24"
          />
        </div>

        <div className="table-container mt-300">
          {error && (
            <p className="text-14 mt-10 text-danger">{error.message}</p>
          )}

          {exemptions?.length > 0 && (
            <table className="bx--data-table table-white mt-20">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student</th>
                  <th>Class</th>
                  <th>Note</th>
                  <th>Date Granted</th>
                  <th>Granted By</th>
                  <th>Date Revoked</th>
                  <th>Revoked By</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {exemptions?.map((exemption) => (
                  <tr key={exemption.id}>
                    <td>{exemption.studentId}</td>
                    <td className="student-name">{exemption.studentName}</td>
                    <td>
                      {studentLookup[exemption.studentId]?.classDescription ||
                        '-'}
                    </td>
                    <td>{exemption.note}</td>
                    <td>{formatDate(exemption.dateGranted)}</td>
                    <td>{exemption.grantedBy}</td>
                    <td>
                      {exemption.dateRevoked
                        ? formatDate(exemption.dateRevoked)
                        : '-'}
                    </td>
                    <td>{exemption.revokedBy || '-'}</td>
                    <td>
                      {!exemption.dateRevoked && (
                        <button
                          className="text-12 text-danger d-flex align-items-center font-bold pointer clean-btn"
                          style={{ gap: '4px' }}
                          onClick={() => setSelectedExemption(exemption)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {exemptions?.length === 0 && !isLoading && (
            <p className="text-14 mt-30 text-grey">No data available.</p>
          )}
        </div>

        {exemptionsData?.pagingInfo?.recordsCount > 0 && (
          <div className="mt-30">
            <Pagination
              backwardText="Previous page"
              forwardText="Next page"
              page={paginate.page}
              pageNumberText="Page Number"
              pageSize={paginate.pageSize}
              pageSizes={[10, 20, 30, 40, 50, 100]}
              totalItems={exemptionsData?.pagingInfo?.recordsCount || 0}
              onChange={(value) => setPaginate(value)}
            />
          </div>
        )}
      </div>

      <GrantExemptionModal
        openModal={isOpen === 'grant'}
        cancelAction={() => setIsOpen('')}
        onSuccess={() => mutate()}
      />

      <RevokeExemptionModal
        openModal={selectedExemption !== null}
        cancelAction={() => setSelectedExemption(null)}
        studentName={selectedExemption?.studentName}
        exemptionId={selectedExemption?.id}
        onSuccess={() => {
          mutate();
          setSelectedExemption(null);
        }}
      />
    </PageLayout>
  );
}
